import React, { useContext, useEffect, useMemo, useState } from "react";
import { Editor } from "@tinymce/tinymce-react";
import * as Contexts from "../../contexts";
import * as Page from "../../components/Page";
import * as Form from "../../components/Form";
import * as Buttons from "../../components/Buttons";
import { MenuItem, Select } from "@material-ui/core";

import { Translater, config } from "../../config";
import { useHttp } from "../../hooks";
import { TabLanguage } from "../../components/TabLanguage/TabLanguage";
import { Snackbar } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { useAgreementLogic } from "./helpers/useAgreementLogic";
import { SelectRestaurant } from "./components";
import { ButtonWrap } from "./styled";

const IndexPage: React.FC = () => {
  const { actions: authActions } = useContext(Contexts.AuthContext);
  const { token } = useContext(Contexts.AuthContext);
  const { language } = useContext(Contexts.LanguageContext);
  const { request, loading } = useHttp();
  const [restaurant, setRestaurant] = useState("");
  const [userAgreementUa, setUserAgreementUa] = useState("");
  const [userAgreementEn, setUserAgreementEn] = useState("");
  const [userPolicyUk, setUserPolicyUk] = useState("");
  const [userPolicyEn, setUserPolicyEn] = useState("");
  const [valueLang, setValueLang] = useState<string>("uk");
  const [isAlertOpen, toogleIsAlertOpen] = useState<boolean>(false);

  const successUpdate = useMemo(() => {
    return (
      (userAgreementEn.length &&
        userAgreementUa.length &&
        userPolicyEn.length &&
        userPolicyUk.length) > 0
    );
  }, [userAgreementEn, userAgreementUa, userPolicyUk, userPolicyEn]);

  const handleEditorChange = (
    content: any,
    editor: any,
    setUserAgreement: (el: string) => void
  ) => {
    setUserAgreement(content);
  };

  const { restaurants, updateRestaron } = useAgreementLogic({
    token: token || "",
    setRestaurant,
    agreementUk: userAgreementUa,
    agreementEn: userAgreementEn,
    setUserPolicyUk,
    setUserPolicyEn,
    restaurant,
  });

  useEffect(() => {
    if (restaurant) {
      const current = restaurants?.find((el) => el._id === restaurant);
      const ukAgreement =
        current?.userAgreement?.find((el) => el.lang === "uk")?.value || "";
      const enAgreement =
        current?.userAgreement?.find((el) => el.lang === "en")?.value || "";

      setUserAgreementUa(ukAgreement);
      setUserAgreementEn(enAgreement);
    }
  }, [restaurant, restaurants]);

  const updateUserAgreement = async () => {
    try {
      await updateRestaron();
      const data = {
        privacyPolicy: [
          {
            value: userPolicyUk,
            lang: "uk",
          },
          {
            value: userPolicyEn,
            lang: "en",
          },
        ],
      };

      if (!(userPolicyUk.length && userPolicyEn.length)) {
        toogleIsAlertOpen(true);
        return;
      }
      toogleIsAlertOpen(true);

      await request(`${config.API}/variables`, "PUT", data, {
        Authorization: `Bearer ${token as string}`,
      });
    } catch (e) {}
  };

  const editorOptionsUa = useMemo(() => {
    return {
      width: "800px",
      height: "400px",
      menubar: false,
      plugins: [
        "advlist autolink lists link image charmap print preview anchor",
        "searchreplace visualblocks code fullscreen",
        "insertdatetime media table paste code help wordcount",
      ],
      toolbar:
        "undo redo | formatselect | " +
        "bold italic backcolor | alignleft aligncenter " +
        "alignright alignjustify | bullist numlist outdent indent | " +
        "removeformat | help",
      setup: function (editor: any) {
        editor.on("change", function () {
          handleEditorChange(editor.getContent(), editor, setUserAgreementUa);
        });
      },
    };
  }, [userAgreementUa]);

  const editorOptionsEn = useMemo(() => {
    return {
      width: "800px",
      height: "400px",
      menubar: false,
      plugins: [
        "advlist autolink lists link image charmap print preview anchor",
        "searchreplace visualblocks code fullscreen",
        "insertdatetime media table paste code help wordcount",
      ],
      toolbar:
        "undo redo | formatselect | " +
        "bold italic backcolor | alignleft aligncenter " +
        "alignright alignjustify | bullist numlist outdent indent | " +
        "removeformat | help",
      setup: function (editor: any) {
        editor.on("change", function () {
          handleEditorChange(editor.getContent(), editor, setUserAgreementEn);
        });
      },
    };
  }, [userAgreementEn]);

  const editorOptionsPrivacyEn = useMemo(() => {
    return {
      width: "800px",
      height: "400px",
      menubar: false,
      plugins: [
        "advlist autolink lists link image charmap print preview anchor",
        "searchreplace visualblocks code fullscreen",
        "insertdatetime media table paste code help wordcount",
      ],
      toolbar:
        "undo redo | formatselect | " +
        "bold italic backcolor | alignleft aligncenter " +
        "alignright alignjustify | bullist numlist outdent indent | " +
        "removeformat | help",
      setup: function (editor: any) {
        editor.on("change", function () {
          handleEditorChange(editor.getContent(), editor, setUserPolicyEn);
        });
      },
    };
  }, [userPolicyEn]);

  const editorOptionsPrivacyUk = useMemo(() => {
    return {
      width: "800px",
      height: "400px",
      menubar: false,
      plugins: [
        "advlist autolink lists link image charmap print preview anchor",
        "searchreplace visualblocks code fullscreen",
        "insertdatetime media table paste code help wordcount",
      ],
      toolbar:
        "undo redo | formatselect | " +
        "bold italic backcolor | alignleft aligncenter " +
        "alignright alignjustify | bullist numlist outdent indent | " +
        "removeformat | help",
      setup: function (editor: any) {
        editor.on("change", function () {
          handleEditorChange(editor.getContent(), editor, setUserPolicyUk);
        });
      },
    };
  }, [userPolicyUk]);

  if (loading) return <Page.Preloader />;

  return (
    <Page.Wrapper title={Translater.SettingsPage.title[language.slug]}>
      <Page.Header title={Translater.SettingsPage.title[language.slug]} />

      <Form.LabelField
        label={Translater.SettingsPage.langSwitchLabel[language.slug]}
        fontSize="small"
        fontWeight="bold"
      >
        <Form.LanguageToggler />
      </Form.LabelField>
      <ButtonWrap>
        <TabLanguage setValueLang={setValueLang} valueLang={valueLang} />
        <SelectRestaurant
          restaurants={restaurants || []}
          setRestaurant={setRestaurant}
          value={restaurant}
          lang={language.label}
        />
      </ButtonWrap>

      {valueLang === "uk" && (
        <Form.LabelField
          fontSize="small"
          label={Translater.TableTitles.userAgreement[language.slug]}
        >
          <Editor
            apiKey={config.editorKey}
            init={editorOptionsUa}
            initialValue={userAgreementUa}
          />
        </Form.LabelField>
      )}
      {valueLang === "en" && (
        <Form.LabelField
          fontSize="small"
          label={Translater.TableTitles.userAgreement[language.slug]}
        >
          <Editor
            apiKey={config.editorKey}
            init={editorOptionsEn}
            initialValue={userAgreementEn}
          />
        </Form.LabelField>
      )}

      {valueLang == "en" && (
        <Form.LabelField
          fontSize="small"
          label={Translater.TableTitles.policy[language.slug]}
        >
          <Editor
            apiKey={config.editorKey}
            init={editorOptionsPrivacyEn}
            initialValue={userPolicyEn}
          />
        </Form.LabelField>
      )}
      {valueLang == "uk" && (
        <Form.LabelField
          fontSize="small"
          label={Translater.TableTitles.policy[language.slug]}
        >
          <Editor
            apiKey={config.editorKey}
            init={editorOptionsPrivacyUk}
            initialValue={userPolicyUk}
          />
        </Form.LabelField>
      )}

      <Buttons.Container
        saveHandler={updateUserAgreement}
        logoutHandler={authActions.LOGOUT}
      />

      <Snackbar
        open={isAlertOpen}
        autoHideDuration={10000}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        onClose={() => toogleIsAlertOpen(false)}
      >
        <Alert severity={successUpdate ? "success" : "error"}>
          {!successUpdate && (
            <p>{Translater.Alert.translationError[language.slug]}</p>
          )}
          {successUpdate && <p>{Translater.SEO.updated[language.slug]}</p>}
        </Alert>
      </Snackbar>
    </Page.Wrapper>
  );
};

export default IndexPage;
